import { useRef, useState } from "react"
import { Column } from "primereact/column"
import { CustomTable, CustomTableRef, Modal, stringFilterTemplate } from "medere-ui-framework"
import CRUDFavoriteStudyModal from "../FavoriteStudies/CRUDFavoriteStudyModal"
import {getAllFavoriteStudies, deleteFavoriteStudy, getApiErrorMessage, toggleFavoriteStudy, getFullFavoriteStudyById, getFullFavoriteStudiesByIds, resolveFavouriteStudyId, FullFavouriteStudyDTO} from "../../servicesHooks/FavoriteStudies/useFavoriteStudiesService"
import "./FavoriteStudies.scss"

interface FavoriteStudiesRow {
  favouritestudy?: number
  favouriteStudy?: number
  favouritestudydesc?: string
  studytype?: string
  composedby?: string
  active?: boolean
  studiesCount?: number
}

function splitComposedByItems(raw: string): string[] {
  if (!raw?.trim()) return []
  const normalized = raw.replace(/\s+/g, " ").trim()

  const byCodePattern = normalized
    .split(/,\s+(?=[A-Za-z0-9][^,]*-\s)/g)
    .map((s: string) => s.trim())
    .filter((s: string) => s.length > 0)

  if (byCodePattern.length > 1) return byCodePattern

  const byTitlePattern = normalized
    .split(/,\s+(?=[A-ZÁÉÍÓÚÑ0-9])/g)
    .map((s: string) => s.trim())
    .filter((s: string) => s.length > 0)

  if (byTitlePattern.length > 1) return byTitlePattern

  return [normalized]
}

function mapDetailItemToLabel(item: any): string {
  const code = (item?.code ?? item?.nomenclatorcode ?? "").toString().trim()
  const description = (item?.description ?? item?.nomenclatordescription ?? "").toString().trim()
  if (code && description) return `${code} - ${description}`
  return description || code || ""
}

export default function FavoriteStudiesView() {
  const tableRef = useRef<CustomTableRef>(null)
  const detailsCacheRef = useRef<Map<number, FullFavouriteStudyDTO>>(new Map())
  const detailsRequestIdRef = useRef(0)
  const [tableReloadKey, setTableReloadKey] = useState(0)
  const [visible, setVisible] = useState(false)
  const [selected, setSelected] = useState<FullFavouriteStudyDTO | null>(null)
  const [isLoadingTable, setIsLoadingTable] = useState(false)
  const [isLoadingBatchDetails, setIsLoadingBatchDetails] = useState(false)
  const [detailsWarning, setDetailsWarning] = useState("")
  const [modalStudies, setModalStudies] = useState<{
    visible: boolean;
    items: string[];
    row?: FavoriteStudiesRow | null;
  }>({
    visible: false,
    items: [],
    row: null,
  })
  const [modalSuccess, setModalSuccess] = useState<{ visible: boolean; message: string }>({
    visible: false,
    message: "",
  })
  const [modalError, setModalError] = useState<{ visible: boolean; message: string }>({
    visible: false,
    message: "",
  })

  const getItems = async (offSet: number, pageSize: number, params?: unknown) => {
    const currentRequestId = ++detailsRequestIdRef.current
    setIsLoadingTable(true)
    setDetailsWarning("")

    try {
      const data = await getAllFavoriteStudies(offSet, pageSize, params)
      const rows = Array.isArray(data) ? (data as FavoriteStudiesRow[]) : []

      const idsInPage = Array.from(
        new Set(
          rows
            .map((row) => resolveFavouriteStudyId(row))
            .filter((id): id is number => !!id && id > 0)
        )
      )

      const missingIds = idsInPage.filter((id) => !detailsCacheRef.current.has(id))
      if (missingIds.length > 0) {
        setIsLoadingBatchDetails(true)
        let batchFailed = false

        try {
          const batchDetails = await getFullFavoriteStudiesByIds(missingIds)
          if (currentRequestId === detailsRequestIdRef.current) {
            batchDetails.forEach((detail) => {
              const id = Number((detail as any)?.favouritestudy ?? 0)
              if (id > 0) detailsCacheRef.current.set(id, detail)
            })
          }
        } catch {
          batchFailed = true
        }

        if (currentRequestId === detailsRequestIdRef.current) {
          const stillMissing = missingIds.filter((id) => !detailsCacheRef.current.has(id))
          if (stillMissing.length > 0) {
            const fallbackResults = await Promise.all(
              stillMissing.map((id) =>
                getFullFavoriteStudyById(id)
                  .then((detail) => ({ id, detail, ok: true as const }))
                  .catch(() => ({ id, detail: null, ok: false as const }))
              )
            )

            if (currentRequestId === detailsRequestIdRef.current) {
              fallbackResults.forEach((result) => {
                if (result.ok && result.detail) detailsCacheRef.current.set(result.id, result.detail)
              })

              const unresolved = fallbackResults.filter((x) => !x.ok).length
              if (batchFailed || unresolved > 0) {
                setDetailsWarning("No se pudieron cargar algunos detalles. La grilla sigue disponible.")
              }
            }
          }
        }
      }

      return rows.map((row) => {
        const parsedCount = splitComposedByItems(row.composedby || "").length
        const favoriteId = resolveFavouriteStudyId(row)
        if (!favoriteId) return { ...row, studiesCount: parsedCount }
        const cached = detailsCacheRef.current.get(favoriteId)
        const realCount = Array.isArray((cached as any)?.items) ? (cached as any).items.length : parsedCount
        return { ...row, studiesCount: realCount }
      })
    } catch {
      setModalError({
        visible: true,
        message: "No se pudieron cargar los estudios favoritos",
      })
    } finally {
      if (currentRequestId === detailsRequestIdRef.current) {
        setIsLoadingTable(false)
        setIsLoadingBatchDetails(false)
      }
    }
  }

  return (
    <div className="p-4 flex flex-column gap-2 favorite-studies flex-grow">
      <h2 className="text-xl font-semibold flex align-items-baseline gap-1">
        <span className="text-xl font-semibold">Estudios o prácticas habituales</span>
        <span className="text-xl text-gray-500">/ configuración de favoritos</span>
      </h2>

      <div className="header-actions">
        <button
          className="btn-create"
          onClick={() => {
            setSelected(null)
            setVisible(true)
          }}
        >
          <i className="pi pi-plus" style={{ marginTop: "2px" }}></i> Crear
        </button>
      </div>
      {isLoadingTable && <small className="text-gray-500">Cargando favoritos...</small>}
      {isLoadingBatchDetails && <small className="text-gray-500">Cargando detalles de prácticas...</small>}
      {!!detailsWarning && <small className="text-orange-600">{detailsWarning}</small>}

      <CustomTable
        key={`favoriteStudiesTable-${tableReloadKey}`}
        idTable="favoriteStudiesTable"
        ref={tableRef}
        getItems={getItems}
        className="favorite-table"
        hideFiltersButton
        showGridlines={false}
        buttonController
        dropdownExtraButtons={[
          {
            icon: "pi pi-file-edit",
            text: "Editar",
            onClick: async (row: FavoriteStudiesRow) => {
            try {
              const favoriteId = Number(row?.favouritestudy ?? row?.favouriteStudy ?? 0)
              if (!favoriteId) {
                setModalError({ visible: true, message: "No se pudo identificar el favorito a editar" })
                return
              }
              const full = detailsCacheRef.current.get(favoriteId) ?? (await getFullFavoriteStudyById(favoriteId))
              detailsCacheRef.current.set(favoriteId, full)

              setSelected(full)
              setVisible(true)
            } catch (err: unknown) {
              setModalError({
                visible: true,
                message: getApiErrorMessage(err, "No se pudo cargar el estudio")
              })
            }
            },
          },
          {
            icon: (row) => row.active ? "pi pi-minus-circle" : "pi pi-check-circle",
            text:   (row) => row.active ? "Desactivar" : "Activar",
            onClick: async (row: FavoriteStudiesRow) => {
              try {
                const favoriteId = resolveFavouriteStudyId(row)
                if (!favoriteId) {
                  setModalError({
                    visible: true,
                    message: "No se pudo identificar el favorito para cambiar el estado"
                  })
                  return
                }
                await toggleFavoriteStudy(favoriteId)
                detailsCacheRef.current.delete(favoriteId)
                setModalSuccess({
                  visible: true,
                  message: row.active
                    ? "Estudio desactivado correctamente"
                    : "Estudio activado correctamente"
                })
                setTableReloadKey((prev) => prev + 1)
                tableRef.current?.refreshTable()
              } catch {
                setModalError({
                  visible: true,
                  message: "No se pudo cambiar el estado"
                })
              }
            }
          },
          {
            icon: "pi pi-trash",
            text: "Eliminar",
            onClick: async (row: FavoriteStudiesRow) => {
              try {
                const favoriteId = resolveFavouriteStudyId(row)
                if (!favoriteId) {
                  setModalError({
                    visible: true,
                    message: "No se pudo identificar el favorito a eliminar"
                  })
                  return
                }
                await deleteFavoriteStudy(favoriteId)
                detailsCacheRef.current.delete(favoriteId)
                setModalSuccess({
                  visible: true,
                  message: "Estudio eliminado correctamente",
                })
                setTableReloadKey((prev) => prev + 1)
                tableRef.current?.refreshTable()
              } catch {
                setModalError({
                  visible: true,
                  message: "No se pudo eliminar el estudio",
                })
              }
            },
          },
        ]}
      >
        <Column field="favouritestudydesc" header="Nombre" filter filterElement={stringFilterTemplate} style={{ width: "200px" }} />
        <Column field="studytype" header="Tipo" filter filterElement={stringFilterTemplate} style={{ width: "160px" }} />
        <Column
          field="composedby"
          header="Estudios / Prácticas"
	          body={(r: FavoriteStudiesRow) => {
	            const items = splitComposedByItems(r.composedby || "")
            const count = Number.isFinite(Number(r.studiesCount)) ? Number(r.studiesCount) : items.length

            const preview = r.composedby?.slice(0, 60) ?? "-"
            const hasMore = r.composedby?.length > 60

            return (
              <div
                className="study-cell clickable"
                title="Ver detalle"
	                onClick={async () => {
	                  try {
	                    const favoriteId = resolveFavouriteStudyId(r)
	                    if (!favoriteId) {
	                      setModalStudies({ visible: true, items, row: r })
	                      return
	                    }

                    const full = detailsCacheRef.current.get(favoriteId) ?? (await getFullFavoriteStudyById(favoriteId))
                    detailsCacheRef.current.set(favoriteId, full)
	                    const fullItems = Array.isArray((full as any)?.items) ? (full as any).items : []
	                    const detailLabels = fullItems
	                      .map((item: any) => mapDetailItemToLabel(item))
	                      .filter((label: string) => label.length > 0)

	                    setModalStudies({
	                      visible: true,
	                      items: detailLabels.length ? detailLabels : items,
	                      row: r
	                    })
	                  } catch {
	                    setModalStudies({ visible: true, items, row: r })
	                  }
	                }}
              >
                <div className="truncate">
                  {preview}
                  {hasMore ? "..." : ""}
                </div>
	                <span className=" favorite-studies view-all">Ver todos ({count})</span>
	              </div>
	            )
	          }}
          filter
          filterElement={stringFilterTemplate}
          style={{ width: "400px" }}
        />
        <Column
          field="active"
          header="Estado"
	          body={(r: FavoriteStudiesRow) => (r.active ? "Activo" : "Inactivo")}
          filter
          filterElement={stringFilterTemplate}
          style={{ width: "120px" }}
        />
      </CustomTable>

      <CRUDFavoriteStudyModal
        visible={visible}
        setVisible={setVisible}
        onSaved={() => {
          setSelected(null)
          detailsCacheRef.current.clear()
          setTableReloadKey((prev) => prev + 1)
          tableRef.current?.refreshTable()
        }}
        studyToEdit={selected}
      />

      <Modal header="Operación exitosa" visible={modalSuccess.visible} setVisible={() => setModalSuccess({ visible: false, message: "" })}>
        {modalSuccess.message}
      </Modal>

      <Modal header="Error" visible={modalError.visible} setVisible={() => setModalError({ visible: false, message: "" })}>
        {modalError.message}
      </Modal>
      <Modal
      header={
        modalStudies.row ? (
          <div className="favorite-studies fav-header">
            <span className="fav-title">
              {modalStudies.row.favouritestudydesc}
            </span>
            <span className="favorite-studies fav-subtitle">
              {modalStudies.row.studytype}
            </span>
          </div>
        ) : (
          "Estudios incluidos"
        )
      }
      visible={modalStudies.visible}
      setVisible={() =>
        setModalStudies({ visible: false, items: [], row: null })
      }
    >
      <ul className="study-list">
        {modalStudies.items.map((item, idx) => (
          <li key={idx}>{item.replace(/\s+/g, " ").trim()}</li>
        ))}
      </ul>
    </Modal>
    </div>
  )
}
